import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { Toaster } from './components/ui/sonner.js';
import './index.css';
import { AuthProvider } from './lib/auth-context.js';

/**
 * Customer SPA bootstrap. Mirrors `apps/admin-console-spa/src/main.tsx`
 * minus MSAL — operators authenticate against Entra; customers
 * authenticate against the Workfront tenant their iframe is embedded in.
 *
 * Auth wiring lands in the `features/auth/` slice — this entry-point
 * stays MSAL-/IdP-agnostic so the auth slice can swap implementations
 * without touching bootstrap.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Customer JWTs are 1h TTL with cookie-backed silent refresh; keep
      // staleness short enough that license-config / branding changes
      // show up across iframe re-mounts but don't thrash app-api.
      staleTime: 30_000,
      retry: 1,
      // Two TanStack-Query defaults we lean on:
      //   • `refetchOnWindowFocus: true` — when the operator tabs
      //     back into the iframe, all stale queries refetch. Plays
      //     well with the dashboard activity panel + scenarios list.
      //   • `refetchIntervalInBackground: false` — pollers stop while
      //     the tab is hidden, then catch up on next focus. The
      //     Recent Activity panel and Planning Backup page both rely
      //     on this to avoid the in-iframe app draining its rate
      //     budget when the operator is on a different tab.
    },
  },
});

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
